import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Checkbox, CircularProgress, IconButton, Button } from '@mui/material';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import BottomSheetDialog from './mobile-bottom-sheet';
import useApi from '../../../hooks/use-api';
import routes from '../../../api/routes';
import buildApiRequestConfig from '../../../api/url-builder';
import { useUser } from '../../../providers/user-provider';
import { useToast } from '../../../providers/toaster-provider';
import DateRangeFilter from '../../ui/date-range-filter';
import TolerancePopover from '../../ui/tolerance-popover';
import { useSettings } from '../../../providers/settings-provider';
import '../../../util/string-prototype';

export interface MatchingTransaction {
    id: string;
    date: string;
    rawDate: Date;
    amount: number;
    description: string;
    selected: boolean;
    bankId?: string | number | null;
}

interface LinkTransactionPopupProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (selectedTransactions: string[]) => void;
    provisionAmount?: number;
    initialToleranceRange?: { min: number; max: number };
    initialDateRange?: { start: string; end: string };
    maxToleranceAmount?: number;
    isProvision?: boolean;
    excludeTransactionId?: string;
    transactionBankId?: string | number | null;
    sourceDescription?: string;
}

// Bottom-sheet phone equivalent of link-transaction.tsx - same fetch/filter
// logic (state/effects below are a close port of that file's own), swapping
// the fixed-width checkbox-row table for a tappable card list and stacking
// the filter controls instead of laying them out side by side. Keep the two
// in sync by hand if the matching/filtering rules change.
const LinkTransactionPopupMobile: React.FC<LinkTransactionPopupProps> = ({
    open,
    onClose,
    onConfirm,
    provisionAmount = 600.00,
    initialToleranceRange = { min: 100, max: 1000 },
    initialDateRange = (() => {
        const today = new Date();
        const oneYearBefore = new Date(today);
        oneYearBefore.setFullYear(today.getFullYear() - 1);
        const oneMonthAfter = new Date(today);
        oneMonthAfter.setMonth(today.getMonth() + 1);
        return {
            start: oneYearBefore.toISOString().split('T')[0],
            end: oneMonthAfter.toISOString().split('T')[0]
        };
    })(),
    maxToleranceAmount = 10000,
    isProvision = false,
    excludeTransactionId,
    transactionBankId,
    sourceDescription
}) => {
    const { user } = useUser();
    const { formatAmount, formatDate } = useSettings();
    const { showError } = useToast();
    const [transactions, setTransactions] = useState<MatchingTransaction[]>([]);
    const [fetch, setFetch] = useApi();

    const [toleranceRange, setToleranceRange] = useState<number>(0);
    const [customMin, setCustomMin] = useState<number | null>(null);
    const [customMax, setCustomMax] = useState<number | null>(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(initialDateRange.start),
        end: new Date(initialDateRange.end)
    });
    const [toleranceAnchorEl, setToleranceAnchorEl] = useState<HTMLElement | null>(null);

    const [allTransactions, setAllTransactions] = useState<MatchingTransaction[]>([]);

    useEffect(() => {
        if (open && user?.entityId) {
            const params = [{ param: 'entityId', value: user.entityId }];
            const { url, method } = buildApiRequestConfig(routes.transaction.fetchByEntity, params);
            setFetch({ url, method, body: {
                showReal: isProvision ? true : false,
                showRecurring: isProvision ? false : true,
                showOneTime: isProvision ? false : true,
                viewBy: 'transactions',
                pageSize: 99999,
            } });
        }
    }, [open, user?.entityId, setFetch, isProvision]);

    useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && data.transactions && !isLoading) {
            const fetchedTransactions = data.transactions?.filter((tx: any) =>
                // Already linked elsewhere, in either direction (see the same
                // filter in link-transaction.tsx) - never offered as a candidate.
                !tx.linkedTransactionId && !tx.hasLinkedTransactions &&
                (!excludeTransactionId || tx.guid !== excludeTransactionId) &&
                (
                    !transactionBankId && !tx.bankId ||
                    transactionBankId && !tx.bankId ||
                    !transactionBankId && tx.bankId ||
                    transactionBankId === tx.bankId
                )
            )?.map((tx: any) => ({
                id: tx.guid,
                date: new Date(tx.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
                rawDate: new Date(tx.date),
                amount: tx.amount,
                description: tx.description?.truncate(40),
                selected: false,
                bankId: tx.bankId,
            }));
            setAllTransactions(fetchedTransactions);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to fetch transactions");
        }
    }, [fetch, showError, excludeTransactionId, transactionBankId]);

    const filteredByTolerance = useMemo(() => {
        const byDate = allTransactions.filter(transaction =>
            transaction.rawDate >= dateRange.start && transaction.rawDate <= dateRange.end
        );

        let byAmount;
        if (customMin !== null && customMax !== null) {
            byAmount = byDate.filter(transaction => transaction.amount >= customMin && transaction.amount <= customMax);
        } else if (toleranceRange === 0) {
            byAmount = byDate.filter(transaction => transaction.amount === provisionAmount);
        } else {
            const minAmount = provisionAmount - toleranceRange;
            const maxAmount = provisionAmount + toleranceRange;
            byAmount = byDate.filter(transaction => transaction.amount >= minAmount && transaction.amount <= maxAmount);
        }

        return [...byAmount].sort((a, b) => Math.abs(a.amount - provisionAmount) - Math.abs(b.amount - provisionAmount));
    }, [allTransactions, toleranceRange, customMin, customMax, provisionAmount, dateRange.start, dateRange.end]);

    useEffect(() => {
        setTransactions(filteredByTolerance);
    }, [filteredByTolerance]);

    useEffect(() => {
        if (open) {
            setTransactions(prev => prev.map(t => ({ ...t, selected: false })));
        } else {
            setToleranceAnchorEl(null);
        }
    }, [open]);

    const handleTransactionToggle = (transactionId: string) => {
        setTransactions(prev =>
            prev.map(transaction =>
                transaction.id === transactionId ? { ...transaction, selected: !transaction.selected } : transaction
            )
        );
    };

    const selectedTransactions = transactions.filter(t => t.selected);
    const selectedTotalAmount = selectedTransactions.reduce((sum, t) => sum + t.amount, 0);

    const resetFilters = () => {
        setTransactions(prev => prev.map(t => ({ ...t, selected: false })));
        setToleranceRange(0);
        setCustomMin(null);
        setCustomMax(null);
        setToleranceAnchorEl(null);
        setDateRange({ start: new Date(initialDateRange.start), end: new Date(initialDateRange.end) });
    };

    const handleConfirm = () => {
        resetFilters();
        onConfirm(selectedTransactions.map(t => t.id));
    };

    const handleCancel = () => {
        resetFilters();
        onClose();
    };

    return (
        <BottomSheetDialog open={open} onClose={handleCancel}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, background: 'var(--color-page-background)', zIndex: 2 }}>
                <IconButton onClick={handleCancel}><ArrowLeft size={22} /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Find Matching Transactions</Typography>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {sourceDescription && (
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                            Matching against: <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'inherit' }}>{sourceDescription}</Typography>
                        </Typography>
                    )}
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={{ fontSize: 13, color: '#707070' }}>Tolerance Range</Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ border: '1px solid #c7c7c7', borderRadius: 2, padding: '6px 10px', cursor: 'pointer' }}
                            onClick={(e) => setToleranceAnchorEl(e.currentTarget)}
                        >
                            <Typography sx={{ fontSize: 13, color: customMin !== null && customMax !== null ? 'var(--color-primary-hover)' : toleranceRange === 0 ? '#a2a2a2' : 'var(--color-primary-hover)' }}>
                                {customMin !== null && customMax !== null
                                    ? `${formatAmount?.(customMin)} - ${formatAmount?.(customMax)}`
                                    : toleranceRange === 0 ? 'Set tolerance' : `± ${toleranceRange}`}
                            </Typography>
                            <ChevronDown size={14} color="#a2a2a2" />
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={{ fontSize: 13, color: '#707070' }}>Date Range</Typography>
                        <DateRangeFilter
                            start={dateRange.start}
                            end={dateRange.end}
                            onChange={(start, end) => setDateRange({ start, end })}
                            sx={{ height: 15, fontSize: 12 }}
                        />
                    </Box>
                    <TolerancePopover
                        open={Boolean(toleranceAnchorEl)}
                        anchorEl={toleranceAnchorEl}
                        onClose={() => setToleranceAnchorEl(null)}
                        toleranceValue={toleranceRange}
                        onToleranceChange={setToleranceRange}
                        onCustomRangeChange={(min, max) => {
                            if (isNaN(min) && isNaN(max)) {
                                setCustomMin(null);
                                setCustomMax(null);
                            } else {
                                setCustomMin(min);
                                setCustomMax(max);
                            }
                        }}
                        minToleranceAmount={0}
                        maxToleranceAmount={maxToleranceAmount}
                        provisionAmount={provisionAmount}
                        customMin={customMin}
                        customMax={customMax}
                    />
                </Box>

                <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {fetch.isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4} gap={1}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Loading transactions...</Typography>
                        </Box>
                    ) : transactions.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <Typography variant="body2" color="text.secondary">No transactions found matching the criteria</Typography>
                        </Box>
                    ) : (
                        transactions.map(transaction => (
                            <Box
                                key={transaction.id}
                                onClick={() => handleTransactionToggle(transaction.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1.25,
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                    background: transaction.selected ? 'rgba(var(--color-primary-rgb), 0.08)' : 'var(--color-container-background)',
                                    cursor: 'pointer',
                                }}
                            >
                                <Checkbox
                                    checked={transaction.selected}
                                    size="small"
                                    onChange={() => handleTransactionToggle(transaction.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ p: 0 }}
                                />
                                <Box flex={1}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(new Date(transaction.date))}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatAmount?.(transaction.amount)}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: '#707070' }}>{transaction.description}</Typography>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, position: 'sticky', bottom: 0, background: 'var(--color-page-background)' }}>
                <Box display="flex" justifyContent="space-between">
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: isProvision ? 'var(--color-provision-text)' : 'var(--color-primary)' }}>
                        {isProvision ? 'Provision' : 'Transaction'} Amount
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: isProvision ? 'var(--color-provision-text)' : 'var(--color-primary)' }}>
                        {formatAmount?.(provisionAmount)}
                    </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Selected Total Amount</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatAmount?.(selectedTotalAmount)}</Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button onClick={handleCancel} variant="outlined" fullWidth>Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        fullWidth
                        disabled={selectedTransactions.length === 0}
                        sx={{ backgroundColor: 'var(--color-primary)', color: '#fff', '&:hover': { backgroundColor: 'var(--color-primary)' } }}
                    >
                        Link Transactions
                    </Button>
                </Box>
            </Box>
        </BottomSheetDialog>
    );
};

export default LinkTransactionPopupMobile;
